import React, { useState } from 'react';
import { FaMapMarker, FaMapPin, FaMapMarkerAlt } from 'react-icons/fa';

const MapControls = ({ 
  showBlmPoints, 
  onShowBlmPointsChange,
  showBlmNames,
  onShowBlmNamesChange, 
  showVendors,
  onShowVendorsChange,
  basemap,
  onBasemapChange,
  isMobile = false,
  blmPointsCount = 0,
  blmNamesCount = 0,
  vendorsCount = 0
}) => {
  const [layerControlsOpen, setLayerControlsOpen] = useState(false);

  return (
    <div style={{
      position: 'absolute',
      top: 20,
      right: 80,
      zIndex: 40,
      display: 'flex',
      flexDirection: 'column',
      gap: 8
    }}>
      {/* Collapsible Layer Control Button */}
      <button
        onClick={() => setLayerControlsOpen(open => !open)}
        style={{
          background: 'white',
          border: '2px solid #374151',
          borderRadius: 10,
          padding: 12,
          fontSize: 14,
          fontWeight: 600,
          color: '#374151',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          width: 48,
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        aria-label="Toggle layer controls"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Expanded Controls Panel */}
      {layerControlsOpen && (
        <div style={{
          position: 'absolute',
          top: 60,
          right: 0,
          background: 'white',
          border: '2px solid #e2e8f0',
          borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          padding: 16,
          minWidth: isMobile ? 200 : 220,
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }}>
          <div style={{ 
            fontSize: 14, 
            fontWeight: 600, 
            color: '#374151',
            marginBottom: 8
          }}>
            Map Layers
          </div>

          {/* Layer Toggle Switches */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: 13, color: '#374151' }}>
              <FaMapMarker style={{ display: 'inline', marginRight: 6, color: '#3b82f6' }} /> BLM Points {showBlmPoints ? `(${blmPointsCount})` : ''}
            </span>
            <button
              onClick={() => onShowBlmPointsChange(!showBlmPoints)}
              style={{
                width: 40,
                height: 20,
                borderRadius: 10,
                border: 'none',
                background: showBlmPoints ? '#3b82f6' : '#d1d5db',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{
                width: 16,
                height: 16,
                borderRadius: 8,
                background: 'white',
                position: 'absolute',
                top: 2,
                left: showBlmPoints ? 22 : 2,
                transition: 'left 0.2s'
              }} />
            </button>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: 13, color: '#374151' }}>
              <FaMapPin style={{ display: 'inline', marginRight: 6, color: '#059669' }} /> BLM Names {showBlmNames ? `(${blmNamesCount})` : ''}
            </span>
            <button
              onClick={() => onShowBlmNamesChange(!showBlmNames)}
              style={{
                width: 40,
                height: 20,
                borderRadius: 10,
                border: 'none',
                background: showBlmNames ? '#059669' : '#d1d5db',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{
                width: 16,
                height: 16,
                borderRadius: 8,
                background: 'white',
                position: 'absolute',
                top: 2,
                left: showBlmNames ? 22 : 2,
                transition: 'left 0.2s'
              }} />
            </button>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: 13, color: '#374151' }}>
              <FaMapMarkerAlt style={{ display: 'inline', marginRight: 6, color: '#9333ea' }} /> Vendors {showVendors ? `(${vendorsCount})` : ''}
            </span>
            <button
              onClick={() => onShowVendorsChange(!showVendors)}
              style={{
                width: 40,
                height: 20,
                borderRadius: 10,
                border: 'none',
                background: showVendors ? '#9333ea' : '#d1d5db',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{
                width: 16,
                height: 16,
                borderRadius: 8,
                background: 'white',
                position: 'absolute',
                top: 2,
                left: showVendors ? 22 : 2,
                transition: 'left 0.2s'
              }} />
            </button>
          </div>

          {/* Basemap Selection */}
          <div style={{ 
            borderTop: '1px solid #e2e8f0', 
            paddingTop: 12,
            marginTop: 8
          }}>
            <div style={{ 
              fontSize: 12, 
              fontWeight: 600, 
              color: '#374151',
              marginBottom: 8
            }}>
              Map Style
            </div>
            <select
              value={basemap}
              onChange={(e) => onBasemapChange(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 12,
                background: 'white',
                color: '#374151'
              }}
            >
              <option value="osm">OpenStreetMap</option>
              <option value="usgs">USGS Topo</option>
              <option value="relief">Relief</option>
              <option value="globe">Globe</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapControls;